import { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { usePage, type SectionProps } from "@/flash/context";
import { arr, cn, rv, str, strs } from "@/flash/ui";
import { GhostLink, Kicker, MaskTitle, Pic, Shell, pad2 } from "./kit";

gsap.registerPlugin(ScrollTrigger);

/* ───────── 02 · Manifesto ───────── */

export function SysManifesto({ title, subtitle, items }: SectionProps) {
  const { images } = usePage();
  const lead = arr(items)[0];
  return (
    <section id="features" className="relative isolate overflow-hidden py-24 md:py-40">
      <Pic
        img={images.manifesto}
        reveal={false}
        className="sx-fade-left absolute inset-y-0 end-0 -z-10 w-full opacity-70 lg:w-[68%] lg:opacity-100"
        imgClass="object-[60%_center]"
        sizes="70vw"
      />
      <div className="sx-vignette absolute inset-0 -z-10" aria-hidden />
      <Shell>
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-8">
          <Kicker className="lg:col-span-5">Platform</Kicker>
          <MaskTitle className="sx-display-xl lg:col-span-7">{str(title)}</MaskTitle>
        </div>
        <div className="mt-14 grid gap-10 md:mt-24 lg:grid-cols-12 lg:gap-8">
          <div {...rv(1)} className="flex max-w-sm flex-col gap-6 lg:col-span-4">
            {subtitle ? <p className="text-[17px] leading-relaxed text-foreground/80">{str(subtitle)}</p> : null}
            {lead ? <p className="text-[15px] leading-relaxed text-muted-foreground">{str(lead.description)}</p> : null}
            <GhostLink to="#showcase" className="mt-2 w-fit">
              Explore capabilities
            </GhostLink>
          </div>
        </div>
        {lead ? (
          <blockquote {...rv(2)} className="font-display mt-24 max-w-3xl text-[clamp(1.75rem,3.4vw,2.9rem)] leading-[1.12] tracking-[-0.03em] text-foreground md:mt-40 lg:ms-[41.66%]">
            &ldquo;{str(lead.title)}. One governed operating layer, not scattered point solutions.&rdquo;
          </blockquote>
        ) : null}
      </Shell>
    </section>
  );
}

/* ───────── 03 · Evidence ───────── */

const BAR = ["78%", "92%", "64%"];
const NUM_RE = /^([+\-−]?)(\d+(?:\.\d+)?)(.*)$/;

export function SysEvidence({ title, subtitle, items }: SectionProps) {
  const { images } = usePage();
  const list = arr(items).slice(0, 3);
  const rootRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const mm = gsap.matchMedia();
    const ctx = gsap.context(() => {
      const planet = root.querySelector<HTMLElement>(".ev-planet img");
      const rows = gsap.utils.toArray<HTMLElement>(".ev-row", root);

      // Planet parallax — transform on the image inside its clipped wrapper (no layout change)
      mm.add(
        {
          desk: "(min-width: 1024px) and (prefers-reduced-motion: no-preference)",
          tab: "(min-width: 768px) and (max-width: 1023px) and (prefers-reduced-motion: no-preference)",
        },
        (c) => {
          if (!planet) return;
          const { desk } = c.conditions as { desk: boolean };
          const y = desk ? 15 : 8;
          gsap.fromTo(
            planet,
            { yPercent: 0, y: -y, scale: 1.04, filter: "brightness(0.82)" },
            {
              y,
              scale: desk ? 1.08 : 1.06,
              filter: "brightness(1)",
              ease: "none",
              scrollTrigger: { trigger: root, start: "top bottom", end: "bottom top", scrub: 0.6 },
            },
          );
        },
      );

      // Sequential metric activation — runs once per page load, never replays to zero
      mm.add("(prefers-reduced-motion: no-preference)", () => {
        if (!rows.length) return;
        const nums = rows.map((r) => r.querySelector<HTMLElement>(".ev-num"));
        const beams = rows.map((r) => r.querySelector<HTMLElement>(".ev-beam"));
        const tips = rows.map((r) => r.querySelector<HTMLElement>(".ev-tip"));
        const finals = nums.map((n) => n?.textContent ?? "");
        gsap.set(rows, { opacity: 0.25, y: 18 });
        gsap.set(beams, { scaleX: 0.08, opacity: 0.35, transformOrigin: "left center" });
        // Marker is a sibling (never scaled): it tracks the beam's visible endpoint via left %
        gsap.set(tips, { left: "8%", opacity: 0.35 });

        const tl = gsap.timeline({ paused: true });
        rows.forEach((row, i) => {
          const at = i * 0.32;
          tl.to(row, { opacity: 1, y: 0, duration: 0.8, ease: "power3.out" }, at);
          tl.to(beams[i], { scaleX: 1, opacity: 1, duration: 1.1, ease: "power2.inOut" }, at + 0.1);
          if (tips[i]) tl.to(tips[i], { left: "100%", opacity: 1, duration: 1.1, ease: "power2.inOut" }, at + 0.1);
          const el = nums[i];
          const m = NUM_RE.exec(finals[i]);
          if (el && m) {
            const target = parseFloat(m[2]);
            const dec = (m[2].split(".")[1] || "").length;
            const o = { v: 0 };
            tl.to(
              o,
              {
                v: target,
                duration: 1,
                ease: "power2.out",
                onUpdate: () => {
                  const shown = o.v.toFixed(dec);
                  const sign = parseFloat(shown) === 0 ? "" : m[1];
                  el.textContent = `${sign}${shown}${m[3]}`;
                },
                onComplete: () => {
                  el.textContent = finals[i];
                },
              },
              at,
            );
          }
        });

        const st = ScrollTrigger.create({
          trigger: root.querySelector(".ev-list") || root,
          start: "top 78%",
          once: true,
          onEnter: () => tl.play(),
        });
        return () => {
          st.kill();
          tl.kill();
          nums.forEach((n, i) => {
            if (n) n.textContent = finals[i];
          });
          gsap.set([...rows, ...beams, ...tips.filter(Boolean)], { clearProps: "all" });
        };
      });
    }, root);
    return () => {
      mm.revert();
      ctx.revert();
    };
  }, []);

  return (
    <section ref={rootRef} id="cases" className="relative isolate overflow-hidden py-24 md:py-36">
      <Shell className="grid gap-14 lg:grid-cols-12 lg:items-center lg:gap-8">
        <Pic img={images.evidence} className="ev-planet sx-fade-radial aspect-[4/5] w-full max-lg:max-h-[520px] lg:col-span-5 lg:-ms-24 lg:aspect-[5/7]" imgClass="will-change-transform" sizes="(min-width:1024px) 45vw, 100vw" />
        <div className="flex flex-col lg:col-span-7 lg:col-start-6 lg:ps-10">
          <Kicker>Impact</Kicker>
          <MaskTitle className="sx-display-md mt-6 max-w-xl">{str(title)}</MaskTitle>
          {subtitle ? (
            <p {...rv(1)} className="mt-5 max-w-md text-[15px] leading-relaxed text-muted-foreground">
              {str(subtitle)}
            </p>
          ) : null}
          <dl className="ev-list mt-14 flex flex-col gap-12 md:mt-20 md:gap-16">
            {list.map((c, i) => (
              <div key={i} className="ev-row flex flex-col gap-5">
                <div className="flex items-end justify-between gap-6">
                  <dd className="ev-num font-display text-[clamp(3.5rem,7.5vw,6.5rem)] font-normal leading-[.85] tracking-[-0.05em] tabular-nums">{str(c.metric)}</dd>
                  <dt className="flex max-w-[15rem] flex-col items-end gap-1 pb-1 text-end">
                    <span className="text-[15px] font-medium text-foreground">{str(c.metricLabel)}</span>
                    <span className="text-[13px] text-muted-foreground">{str(c.title)}</span>
                  </dt>
                </div>
                <div className="relative h-px w-full bg-white/10">
                  <span className="absolute inset-y-0 left-0" style={{ width: BAR[i % BAR.length] }} aria-hidden>
                    <span className="ev-beam sx-beam absolute inset-0" />
                    <span className="ev-tip sx-beam-tip" style={{ left: "100%" }} />
                  </span>
                </div>
              </div>
            ))}
          </dl>
        </div>
      </Shell>
    </section>
  );
}

/* ───────── 04 · Capability chapters → see chapters.tsx ───────── */

/* ───────── 05 · Why the system works ───────── */

const STEP = ["lg:col-start-7", "lg:col-start-5", "lg:col-start-3", "lg:col-start-1"];

export function SysSystem({ title, subtitle, items }: SectionProps) {
  const { images } = usePage();
  const list = arr(items).slice(0, 4);
  const rootRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const mm = gsap.matchMedia();
    mm.add(
      {
        desk: "(min-width: 1024px) and (prefers-reduced-motion: no-preference)",
        tab: "(min-width: 768px) and (max-width: 1023px) and (prefers-reduced-motion: no-preference)",
      },
      (c) => {
        const { desk } = c.conditions as { desk: boolean };
        const bg = root.querySelector<HTMLElement>(".sy-bg img");
        // Background depth: scale stays >= 1.02 so the ±y travel never exposes edges
        if (bg) {
          const y = desk ? 10 : 5;
          gsap.fromTo(
            bg,
            { y: -y, scale: 1.02, filter: "brightness(0.86)" },
            {
              y,
              scale: desk ? 1.06 : 1.04,
              filter: "brightness(1)",
              ease: "none",
              scrollTrigger: { trigger: root, start: "top bottom", end: "bottom top", scrub: 0.6 },
            },
          );
        }
        if (!desk) return;
        // Sequential focus, scrubbed (fully reversible), ends balanced
        const plates = gsap.utils.toArray<HTMLElement>(".sy-plate", root);
        const inner = plates.map((p) => p.querySelector<HTMLElement>(".sy-focus"));
        if (!plates.length) return;
        gsap.set(plates, { "--sf": 0 });
        gsap.set(inner, { opacity: 0.84, y: 0 });
        const tl = gsap.timeline({
          defaults: { ease: "sine.inOut", duration: 1 },
          scrollTrigger: { trigger: root.querySelector(".sy-grid") || root, start: "top 80%", end: "bottom 35%", scrub: 0.8 },
        });
        plates.forEach((p, i) => {
          const at = i * 1.4;
          tl.to(p, { "--sf": 1 }, at).to(inner[i], { opacity: 1, y: -5 }, at);
          if (i < plates.length - 1) tl.to(p, { "--sf": 0.25 }, at + 1.4).to(inner[i], { opacity: 0.92, y: 0 }, at + 1.4);
        });
        const endAt = plates.length * 1.4;
        tl.to(plates, { "--sf": 0.25 }, endAt).to(inner, { opacity: 1, y: 0 }, endAt);
        return () => gsap.set([...plates, ...inner], { clearProps: "all" });
      },
    );
    return () => mm.revert();
  }, []);

  return (
    <section ref={rootRef} id="system" className="relative isolate overflow-hidden py-24 md:py-40">
      <Pic img={images.system} reveal={false} className="sy-bg absolute inset-0 -z-20" imgClass="object-center will-change-transform" sizes="100vw" />
      <div className="sx-system-veil absolute inset-0 -z-10" aria-hidden />
      <Shell>
        <div className="flex max-w-xl flex-col gap-6">
          <Kicker>Why it works</Kicker>
          <MaskTitle className="sx-display-md">{str(title)}</MaskTitle>
          {subtitle ? <p {...rv(1)} className="text-[15px] leading-relaxed text-foreground/70">{str(subtitle)}</p> : null}
        </div>
        <div className="sy-grid mt-16 grid gap-4 md:mt-24 lg:grid-cols-12 lg:gap-x-8 lg:gap-y-5">
          {list.map((f, i) => (
            <article key={i} {...rv(i)} className={cn("sx-plate sy-plate lg:col-span-6", STEP[i])}>
              <div className="sy-hover">
              <div className="sy-focus flex flex-col gap-3 p-6 md:p-8">
              <div className="flex items-baseline justify-between gap-4">
                <h3 className="font-display text-[clamp(1.35rem,2vw,1.7rem)] leading-tight tracking-[-0.02em]">{str(f.title)}</h3>
                <span className="text-[12px] tabular-nums text-muted-foreground">{pad2(i + 1)}</span>
              </div>
              <p className="max-w-md text-[15px] leading-relaxed text-foreground/70">{str(f.description)}</p>
              </div>
              </div>
            </article>
          ))}
        </div>
      </Shell>
    </section>
  );
}